/* eslint-disable import/no-named-as-default-member */
import React, { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useSoyuzLocales } from '@spotahome/soyuz/client';
import Tracking from '@spotahome/soyuz-tracking';

import { getActiveBanners } from '@spotahome/marketplace-common/src/components/HomeCardBanner/config';
import HomeCardBanner from '@spotahome/marketplace-common/src/components/HomeCardBanner';
import { isMobileScreen } from '@spotahome/marketplace-common/src/utils/screenWidth';

import Homecard from '@spotahome/marketplace-common/src/components/Homecard';

import IconLoading from '@spotahome/marketplace-common/src/LEGACY/javascripts/ui/icon/icon-loading';

import { getCardsFromIds } from '../../utils/getCardsFromIds';

import { useHighlightMarkersContext } from '../../context/HighlightMarkersContext';

import AlertsCreationReminder from '../AlertsCreationReminder/AlertsCreationReminder';

import homecardListTracking from './utils/homecardListTracking';

import EmptyList from './EmptyList';

import './HomeCardList.scss';

const hasNewMarkers = (oldMarkers, newMarkers) =>
  JSON.stringify(oldMarkers.map(e => e.id)) !==
  JSON.stringify(newMarkers.map(e => e.id));

const scrollPageToTop = () => {
  document.querySelector('html').scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  });
};

export const HomeCardList = ({
  markerList,
  initialHomecards,
  cityId,
  filters,
  hidePriceWithoutDates,
  cancellationPolicies,
  hasZeroResults,
  preloadedHomecards,
  hasLazyLoad,
  showBillsIncludedLabel,
  showAlertCreationReminder
}) => {
  const prevMarkers = useRef(markerList);
  const homecardListRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [homecards, setHomecards] = useState(initialHomecards);
  const {
    trackInitialMount,
    trackHomecardClick,
    trackProductImpressions,
    triggerHotjarRecording
  } = useMemo(() => homecardListTracking(cityId), []);
  const { handleCardMouseEnter, handleCardMouseLeave, markerClickedId } =
    useHighlightMarkersContext();
  const { current: currentLocale } = useSoyuzLocales();

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    trackInitialMount(markerList, homecards);
  }, []);

  useEffect(() => {
    if (markerList.length === 0 && !isLoading) {
      Tracking.ga.sendEvent('search-page', 'no-results-found');
    }
  }, [markerList, isLoading]);

  useEffect(() => {
    const requestNewHomecards = async () => {
      setIsLoading(true);
      const ids = markerList.map(card => card.id);
      const newHomecards = await getCardsFromIds(ids, currentLocale);
      trackProductImpressions(markerList, newHomecards);
      triggerHotjarRecording(homecards);

      setHomecards(newHomecards);
      prevMarkers.current = markerList;

      setIsLoading(false);
    };

    if (hasNewMarkers(prevMarkers.current, markerList)) {
      scrollPageToTop();
      requestNewHomecards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerList]);

  const renderHomecardList = () => {
    const shouldShowAvailableFrom = !filters['move-in'];

    const shouldShowPrice = !(hidePriceWithoutDates && !filters['move-in']);
    const [activeBanner] = getActiveBanners(cityId);

    const availableHomecards = markerList.filter(({ id }) => homecards[id]);

    return availableHomecards.map((homecard, position) => {
      const cardData = homecards[homecard.id];
      const cancellationPolicy =
        cancellationPolicies[homecard.cancellationType] || {};

      const firstAvailableDate = new Date(cardData.firstAvailableDate);
      const firstAvailableYear = firstAvailableDate.getFullYear();
      return (
        <Fragment key={`homecard-${homecard.id}`}>
          {activeBanner && activeBanner.position === position ? (
            <HomeCardBanner
              name={activeBanner.name}
              component={activeBanner.component}
              bannerConfig={activeBanner.componentConfig}
              bannerTracking={activeBanner.tracking}
              isOneColumnView={
                homecardListRef.current.clientWidth &&
                homecardListRef.current.firstChild.clientWidth ===
                  homecardListRef.current.clientWidth
              }
            />
          ) : null}

          <div className="l-list__item" data-homecard-scroll={homecard.id}>
            <Homecard
              isShowStatus
              data={cardData}
              freeCancellationDeadline={cancellationPolicy}
              isFreeCancellationVariant
              moveIn={filters['move-in']}
              moveOut={filters['move-out']}
              flexibleMoveOut={!!filters.flexibleMoveOut}
              flexibleMoveIn={!!filters.flexibleMoveIn}
              onCardMouseEnter={handleCardMouseEnter}
              onCardMouseLeave={handleCardMouseLeave}
              onClick={() => trackHomecardClick(position, cardData)}
              // pointOfInterest={this.props.pointOfInterest}
              reviews={cardData.reviews}
              selected={homecard.id === markerClickedId}
              hasLazyLoad={
                preloadedHomecards.includes(cardData.id) ? false : hasLazyLoad
              }
              showBillsIncludedLabel={showBillsIncludedLabel}
              showAvailableFrom={shouldShowAvailableFrom}
              showPrice={shouldShowPrice}
              newTab={!isMobileScreen()}
              zoomOnHover
              showAvailableFromYear={firstAvailableYear !== currentYear}
              pageType="search"
            />
          </div>
        </Fragment>
      );
    });
  };

  const hasHomecards = markerList && Object.keys(homecards).length;

  return (
    <div
      data-cy="property-list"
      data-test="property-list"
      className="l-list"
      ref={homecardListRef}
    >
      {isLoading ? <EmptyList /> : null}
      {!isLoading && hasHomecards ? renderHomecardList() : null}
      {(!isLoading && !hasHomecards) || showAlertCreationReminder ? (
        <AlertsCreationReminder
          cityId={cityId}
          filters={filters}
          hasZeroResults={hasZeroResults}
        />
      ) : null}
      <div className="spinner-loading-map" hidden={!isLoading}>
        <IconLoading />
      </div>
    </div>
  );
};

const freeCancellationCurrentlyShape = {
  freeCancellationCurrently: PropTypes.string
};

HomeCardList.propTypes = {
  markerList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialHomecards: PropTypes.shape({}),
  filters: PropTypes.shape({
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    flexibleMoveOut: PropTypes.number,
    flexibleMoveIn: PropTypes.number
  }),
  cityId: PropTypes.string.isRequired,
  cancellationPolicies: PropTypes.shape({
    flexible: PropTypes.shape(freeCancellationCurrentlyShape),
    standard: PropTypes.shape(freeCancellationCurrentlyShape),
    strict: PropTypes.shape(freeCancellationCurrentlyShape)
  }),
  pointOfInterest: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  hasLazyLoad: PropTypes.bool,
  showBillsIncludedLabel: PropTypes.bool,
  hidePriceWithoutDates: PropTypes.bool,
  showAlertCreationReminder: PropTypes.bool,
  hasZeroResults: PropTypes.bool,
  preloadedHomecards: PropTypes.arrayOf(PropTypes.string)
};

HomeCardList.defaultProps = {
  cancellationPolicies: {},
  filters: {},
  initialHomecards: {},
  pointOfInterest: {},
  hasLazyLoad: false,
  showBillsIncludedLabel: false,
  hidePriceWithoutDates: false,
  showAlertCreationReminder: false,
  hasZeroResults: false,
  preloadedHomecards: []
};

export default HomeCardList;
